import { Fragment, useState, type ReactNode } from 'react';
import { Link } from '../../components/nav';
import { SourceBadge } from '../../components/SourceBadge';
import { Tabs } from '../../components/Tabs';
import { ATTRIBUTION_LABEL, CONFIDENCE_LABEL, PLACE_TYPE_LABEL, PRECISION_LABEL, SOURCE_KIND, STATUS_LABEL, type Mention, type PlaceFeature } from '../../data/types';
import { useAppData } from '../../state/AppData';
import { useSelection } from '../../state/selection';
import { chapterRoman } from '../reader/segments';


export function PlaceCard() {
  const sel = useSelection();
  const { placeById } = useAppData();
  const place = sel.place ? placeById.get(sel.place) : undefined;
  if (!place) return <Empieza />;
  return <PlaceCardBody key={place.id} place={place} />;
}

/** Lo que se ve antes de elegir nada. Era una frase en cursiva pidiendo que se pulsara algo; quien
 *  no sabe qué hacer no necesita una lista de opciones, necesita un primer paso. Así que además de
 *  decir qué hace cada gesto, ofrece el lugar más presente del libro para empezar por él. */
function Empieza() {
  const sel = useSelection();
  const { places, mentionsByPlace, itineraries, city } = useAppData();
  const destacado = [...places]
    .filter((p) => p.geometry)
    .sort((a, b) => (mentionsByPlace.get(b.id)?.length ?? 0) - (mentionsByPlace.get(a.id)?.length ?? 0))[0];
  const itin = itineraries[0];
  return (
    <aside className="card card--empty empieza" aria-label="Ficha de lugar">
      <h2>Tres maneras de empezar</h2>
      <p className="empieza__sub">Elige una; siempre puedes volver.</p>
      <ol>
        <li><b>1</b><span>Pulsa un nombre resaltado del texto<em>Se señala en el mapa y verás qué queda hoy</em></span></li>
        <li><b>2</b><span>Pulsa un punto del mapa<em>Te lleva al pasaje donde Quadrado lo describe</em></span></li>
        <li><b>3</b><span>Sigue un itinerario guiado<em>{itin ? `${itin.title} · ${itin.durationMinutes} min` : `Recorridos por ${city.inline}`}</em></span></li>
      </ol>
      <p className="empieza__acciones">
        {destacado && (
          <button type="button" className="btn btn--cta" onClick={() => sel.selectPlace(destacado.id, 'card')}>
            Empezar por {destacado.properties.canonicalName} →
          </button>
        )}
        {itin && <Link to={`/itinerarios/${itin.slug}`}>Ver el itinerario</Link>}
      </p>
    </aside>
  );
}

function PlaceCardBody({ place }: { place: PlaceFeature }) {
  const [tab, setTab] = useState('quadrado');
  const sel = useSelection();
  const { mentionsByPlace } = useAppData();
  const pr = place.properties;
  const mentions = mentionsByPlace.get(place.id) ?? [];
  const curIdx = mentions.findIndex((m) => m.id === sel.mention);

  return (
    <aside className="card" aria-labelledby="card-title">
      <header className="card__head">
        <h2 id="card-title">{pr.canonicalName}</h2>
        <button type="button" className="card__close" onClick={() => sel.selectPlace(null, 'card')} aria-label="Cerrar ficha">×</button>
        <p className="card__names">En Quadrado: {pr.quadradoNames.map((n) => <q key={n}>{n}</q>).reduce<ReactNode[]>((acc, x, i) => (i ? [...acc, ', ', x] : [x]), [])}</p>
        <ul className="chips" aria-label="Clasificación">
          <li>{PLACE_TYPE_LABEL[pr.type] ?? pr.type}</li>
          <li className={`chip--status chip--${pr.currentStatus}`}>Estado actual: {STATUS_LABEL[pr.currentStatus]}</li>
          <li>Localización: {PRECISION_LABEL[pr.geometryPrecision]}</li>
          <li>Identidad: {CONFIDENCE_LABEL[pr.identityConfidence]}</li>
        </ul>
      </header>
      {mentions.length > 0 && (
        // La pista del mapa promete «ver qué dice el libro», y al pulsar un punto aparecía esta
        // ficha con la lista de pasajes: el usuario se quedaba a medio camino sin saberlo. Esta es
        // la acción que cierra la promesa, arriba del todo y con el número de pasajes a la vista.
        <p className="card__leer">
          <button type="button" className="btn btn--cta" onClick={() => sel.readInContext(mentions[curIdx >= 0 ? curIdx : 0])}>
            Leer {curIdx >= 0 ? 'este pasaje' : 'el primer pasaje'} en el libro →
          </button>
          <span>{mentions.length === 1 ? 'Es la única vez que lo menciona.' : 'Los demás pasajes están en la lista de abajo.'}</span>
        </p>
      )}
      <div className="card__mentionnav">
        <span>{mentions.length} {mentions.length === 1 ? 'mención' : 'menciones'}{curIdx >= 0 ? ` · leyendo la ${curIdx + 1}.ª` : ''}</span>
        <span>
          <button type="button" disabled={mentions.length === 0 || curIdx <= 0} onClick={() => sel.readInContext(mentions[Math.max(0, curIdx - 1)])}>← Anterior</button>
          <button type="button" disabled={mentions.length === 0 || curIdx === mentions.length - 1} onClick={() => sel.readInContext(mentions[curIdx + 1] ?? mentions[0])}>{curIdx < 0 ? 'Primera mención →' : 'Siguiente →'}</button>
        </span>
      </div>
      <Tabs label="Información del lugar" active={tab} onChange={setTab} className="card__tabs" tabs={[
        { id: 'quadrado', label: 'Quadrado', content: <MentionList mentions={mentions} currentId={sel.mention} /> },
        { id: 'hoy', label: 'Hoy', content: <Today place={place} /> },
        { id: 'datos', label: 'Datos', content: <DataTab place={place} /> },
      ]} />
    </aside>
  );
}

function MentionList({ mentions, currentId }: { mentions: Mention[]; currentId: string | null }) {
  const sel = useSelection();
  if (mentions.length === 0) return <p>Sin menciones publicadas.</p>;
  return (
    <>
      <SourceBadge kind="quadrado" detail="texto de 1884" />
      <ol className="mentions">
        {mentions.map((m) => {
          const chapter = m.paragraphId.slice(0, 7);
          const isNote = /-n\d+$/.test(m.paragraphId);
          return (
            <li key={m.id} className={m.id === currentId ? 'is-current' : undefined} aria-current={m.id === currentId ? 'true' : undefined}>
              <span className="mentions__loc">p. {m.printedPage ?? '—'} · cap. {chapterRoman(chapter)}{isNote ? ' · nota' : ''}</span>
              <span className="mentions__excerpt">…{m.contextBefore}<mark>{m.surfaceForm}</mark>{m.contextAfter}…</span>
              <button type="button" className="link-button" onClick={() => sel.readInContext(m)}>Leer en contexto<span className="visually-hidden"> (página {m.printedPage})</span></button>
            </li>
          );
        })}
      </ol>
    </>
  );
}

function Today({ place }: { place: PlaceFeature }) {
  const pr = place.properties;
  return (
    <div className="today">
      {pr.jcyl.length === 0 ? (
        <p className="today__none">
          <SourceBadge kind="jcyl" /> Sin registro en la «Relación de monumentos» (consulta del 17-09-2026).
          La ausencia en este dataset no demuestra que el edificio haya desaparecido.
        </p>
      ) : pr.jcyl.map((r) => (
        <section key={r.externalId} className="today__rec">
          <SourceBadge kind="jcyl" detail={`registro ${r.externalId} · CC BY 4.0 · consulta ${r.retrievedAt}`} />
          <h3>{r.nombre}</h3>
          <dl className="kv">
            <dt>Tipo</dt><dd>{r.tipoMonumento} · {r.clasificacion}</dd>
            {r.periodoHistorico && <><dt>Periodo</dt><dd>{r.periodoHistorico}</dd></>}
            {r.calle && <><dt>Dirección</dt><dd>{r.calle}</dd></>}
            {r.bicIds.length > 0 && <><dt>Bien de Interés Cultural</dt><dd>ID {r.bicIds.join(', ')}</dd></>}
          </dl>
          {r.descripcion_texto && <p className="today__desc">{r.descripcion_texto}</p>}
          <p><a href={r.url} target="_blank" rel="noopener noreferrer">Ver en el portal de datos abiertos ↗</a></p>
        </section>
      ))}
      {pr.statusAssertions.map((a) => (
        <section key={a.id} className="today__status">
          <SourceBadge kind={SOURCE_KIND[a.sourceId] ?? 'editorial'} detail={[a.sourceLocator, ATTRIBUTION_LABEL[a.attributionType]].filter(Boolean).join(' · ')} />
          <h3>Estado respecto a 1884: {STATUS_LABEL[a.value] ?? a.value}</h3>
          <p className="today__conf">Confianza: {CONFIDENCE_LABEL[a.confidence] ?? a.confidence}</p>
          {a.note && <p>{a.note}</p>}
        </section>
      ))}
      {pr.geometrySource === 'osm_overpass_2026_09_17' && (
        <section className="today__osm">
          <SourceBadge kind="osm" detail="ODbL 1.0 · consulta 2026-09-17" />
          <p>
            Ubicación tomada de OpenStreetMap{pr.geometryPrecision === 'approximate' ? ' (aproximada: el edificio ya no existe o no se conoce su planta exacta)' : pr.geometryPrecision === 'vicinity' ? ' (centro del espacio urbano)' : pr.geometryPrecision === 'municipality' ? ' (solo el término municipal: no consta dónde estaba dentro del pueblo)' : ''}.
            {pr.geometryNote ? ` ${pr.geometryNote}` : ''}
          </p>
          {pr.externalRecords.filter((e) => e.url && e.sourceId !== 'jcyl_relacion_monumentos').map((e) => (
            <p key={e.externalId}><a href={e.url} target="_blank" rel="noopener noreferrer">Ver {e.sourceId === 'wikidata' ? `${e.externalId} en Wikidata` : 'el elemento en OpenStreetMap'} ↗</a></p>
          ))}
        </section>
      )}
      {pr.editorialSummary && (
        <section className="today__editorial">
          <SourceBadge kind="editorial" />
          <p>{pr.editorialSummary}</p>
          {pr.currentStatus === 'undetermined' && <p>El estado actual no se afirma hasta contar con una fuente que lo documente.</p>}
        </section>
      )}
    </div>
  );
}

function DataTab({ place }: { place: PlaceFeature }) {
  const pr = place.properties;
  return (
    <div>
      <dl className="kv">
        <dt>Identificador</dt><dd><code>{pr.id}</code></dd>
        <dt>Identidad</dt><dd>{CONFIDENCE_LABEL[pr.identityConfidence]}</dd>
        <dt>Coordenadas</dt><dd>{place.geometry ? `${place.geometry.coordinates[1].toFixed(5)}, ${place.geometry.coordinates[0].toFixed(5)} (WGS84)` : 'sin localización publicada'}</dd>
        <dt>Precisión</dt><dd>{PRECISION_LABEL[pr.geometryPrecision]}</dd>
        {pr.externalRecords.map((e) => (
          <Fragment key={e.sourceId + e.externalId}><dt>{e.sourceId === 'jcyl_relacion_monumentos' ? 'Correspondencia' : 'Registro externo'}</dt><dd>{e.url ? <a href={e.url} target="_blank" rel="noopener noreferrer">{e.sourceId} #{e.externalId}</a> : <>{e.sourceId} #{e.externalId}</>}{e.matchScore !== undefined ? ` · puntuación ${e.matchScore.toFixed(2)}` : ''}{e.matchNote ? <><br /><small>{e.matchNote}</small></> : null}</dd></Fragment>
        ))}
        <dt>Revisión</dt><dd>{pr.lastReviewed ?? '—'}</dd>
      </dl>
      <p><a href={`${import.meta.env.BASE_URL}data/places.geojson`} download>Descargar el dataset derivado (GeoJSON, ODbL 1.0)</a> · <Link to="/proyecto/fuentes">Método</Link></p>
    </div>
  );
}
